import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { Serialiser } from "hooks/serialisers/types";

dayjs.extend(utc);

export const snapToInterval = (
  time: Dayjs,
  dayStart: Dayjs,
  intervalSeconds: number,
  direction: "floor" | "round" | "ceil" = "round"
): Dayjs => {
  const secondsFromDayStart = time.diff(dayStart, "second", true);
  let snappedSeconds: number;

  switch (direction) {
    case "floor":
      snappedSeconds =
        Math.floor(secondsFromDayStart / intervalSeconds) * intervalSeconds;
      break;
    case "ceil":
      snappedSeconds =
        Math.ceil(secondsFromDayStart / intervalSeconds) * intervalSeconds;
      break;
    case "round":
    default:
      snappedSeconds =
        Math.round(secondsFromDayStart / intervalSeconds) * intervalSeconds;
      break;
  }

  return dayStart.add(snappedSeconds, "second");
};

export const FIVE_MINUTES_IN_SECONDS = 5 * 60;

export const SECONDS_IN_DAY = 24 * 60 * 60;

export const calculateSnappedMaxStart = (
  dayStart: Dayjs,
  dayEnd: Dayjs,
  windowMinutes: number
): Dayjs => {
  const maxStartRaw = dayEnd.subtract(windowMinutes, "minutes");
  return snapToInterval(
    maxStartRaw,
    dayStart,
    FIVE_MINUTES_IN_SECONDS,
    "floor"
  );
};

export const clampToDay = (time: Dayjs, dayStart: Dayjs, dayEnd: Dayjs) => {
  if (time.isBefore(dayStart)) return dayStart;
  if (time.isAfter(dayEnd)) return dayEnd;
  return time;
};

export const clampWindowToDay = (
  windowStart: Dayjs,
  windowEnd: Dayjs,
  dayStart: Dayjs,
  dayEnd: Dayjs
) => {
  const clampedStart = windowStart.isBefore(dayStart) ? dayStart : windowStart;
  const clampedEnd = windowEnd.isAfter(dayEnd) ? dayEnd : windowEnd;

  return { start: clampedStart, end: clampedEnd };
};

export const calculateFocusedWindow = (
  focusTimestamp: Dayjs,
  dayStart: Dayjs,
  dayEnd: Dayjs,
  windowMinutes: number
): { start: Dayjs; end: Dayjs } => {
  const rawStart = focusTimestamp.subtract(windowMinutes / 2, "minutes");
  const snappedStart = snapToInterval(
    rawStart,
    dayStart,
    FIVE_MINUTES_IN_SECONDS,
    "floor"
  );
  const maxStart = calculateSnappedMaxStart(dayStart, dayEnd, windowMinutes);
  const clampedStart = clampToDay(snappedStart, dayStart, maxStart);
  return {
    start: clampedStart,
    end: clampedStart.add(windowMinutes, "minutes"),
  };
};

export const canMoveBackward = (
  viewingWindowStart: Dayjs,
  dayStart: Dayjs
): boolean => {
  return viewingWindowStart.isAfter(dayStart);
};

export const canMoveForward = (
  viewingWindowEnd: Dayjs,
  dayEnd: Dayjs
): boolean => {
  return viewingWindowEnd.isBefore(dayEnd);
};

export const dayjsSerializer: Serialiser<Dayjs, never> = {
  format: (value) => value.utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
  parse: (text) => ({ valid: true, value: dayjs.utc(text) }),
};

export const parseFocusTimestampParam = (
  dateParam: string | null
): Dayjs | null => {
  // Only a full timestamp deep-links a focus moment to centre the window on; a date-only value just selects the day.
  if (!dateParam?.includes(":")) return null;
  const parsed = dayjs.utc(dateParam);
  return parsed.isValid() ? parsed : null;
};
